import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import Image from '@base/Image';
import Button from '@base/Button/Button';
import { setModalState } from '@actions/quizTaking';
import { getIsLoggedIn } from '@selectors/auth';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { mediaQuery, Spacings } from '@styles/index';
import { MODAL_TYPES, ENGLISH_LANGUAGE_PREFERENCE } from '@cont/Quiz/constants';
import { useDSTMetaInfoStore } from '@stores/dst/dstStore';
import { START_TEST_CLICKED } from '@constants/segment';
import { useRouter } from 'next/router';
import useDSTSegmentAnalytics from '@cont/DST/Common/useDSTSegmentAnalytics';
import { noOp } from '@constants/empty';
import { useIsMobile } from '@stores/AppCommonData';
import { useEffect, useRef } from 'react';
import isEmptyObject from '@utils/isEmptyObject';
import useFollowGoal from '@api/hooks/useFollowGoal';
import useDSTActions from '@cont/DST/useDSTActions';
import { DST_MAX_ATTEMPT_TIMES } from '@cont/DST/constants';
import { segment } from '@actions/analytics';
import { LOGIN_STARTED } from '@cont/Login/constants';

const FileIcon = 'quiz/file.svg';

const Wrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_5B};
  margin-top: ${Spacings.SPACING_12B};

  ${mediaQuery.uptoMobile} {
    flex-flow: column;
    text-align: center;
  }
`;

const StyledButton = styled(Button)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const Actions = ({ loading }) => {
  const {
    query: { goalUID }
  } = useRouter();
  const dispatch = useDispatch();
  const { classPreference, attemptsInfo } = useDSTMetaInfoStore();
  const { handleTestStart, loading: startTestLoading } = useDSTActions();

  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);

  const isLoggedIn = useSelectorWithShallowEqual(getIsLoggedIn);
  const segmentAnalytics = useDSTSegmentAnalytics();
  const isMobile = useIsMobile();
  const isCTAClickedRef = useRef(false);

  const showInstructions = (showFooter = false, onClick = noOp) => {
    dispatch(
      setModalState({
        type: MODAL_TYPES.QUIZ_INSTRUCTION,
        data: {
          showFooter,
          onClick
        }
      })
    );
  };

  const sendAnalyticsOnEnroll = () => segmentAnalytics(START_TEST_CLICKED);

  const getClassUIDForMobileDST = () => {
    const defaultClassUID =
      Array.isArray(classPreference?.preferenceList) &&
      classPreference?.preferenceList &&
      classPreference?.preferenceList[0]?.uid;
    return classPreference?.isPreferenceAvailable
      ? classPreference?.classPreference
      : defaultClassUID;
  };

  const startTestOnClick = () =>
    handleTestStart({
      languageInfo: ENGLISH_LANGUAGE_PREFERENCE,
      classUID: getClassUIDForMobileDST(),
      isReattempt: attemptsInfo?.attemptsLeft < DST_MAX_ATTEMPT_TIMES
    });

  const { followCurrentGoal } = useFollowGoal(goalUID);

  const handleScholarshipEnroll = async () => {
    sendAnalyticsOnEnroll();

    if (classPreference?.isPreferenceAvailable) {
      return dispatch(
        setModalState({
          type: MODAL_TYPES.QUIZ_LANGUAGE_SELECTION,
          data: {
            classUID: classPreference?.classPreference
          }
        })
      );
    }

    dispatch(
      setModalState({
        type: MODAL_TYPES.QUIZ_CLASS_SELECTION,
        data: {}
      })
    );
  };

  useEffect(() => {
    if (
      isCTAClickedRef.current &&
      isLoggedIn &&
      !isEmptyObject(classPreference)
    ) {
      isCTAClickedRef.current = false;
      if (isMobile) {
        sendAnalyticsOnEnroll();
        startTestOnClick();
      } else {
        showInstructions(true, handleScholarshipEnroll);
      }

      followCurrentGoal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, classPreference]);

  const handleOnEnrollClick = () => {
    if (!isLoggedIn) {
      isCTAClickedRef.current = true;
      segment.track(LOGIN_STARTED, {
        last_primary_source: 'Home',
        last_primary_source_section: 'Scholarship Test'
      });
      showLoginDrawer();
      return;
    }

    if (isMobile) {
      sendAnalyticsOnEnroll();
      startTestOnClick();
    } else {
      showInstructions(true, handleScholarshipEnroll);
    }
    followCurrentGoal();
  };

  const handleOnInstructionsClick = () => showInstructions();

  const isLoading = isMobile ? startTestLoading : loading;

  return (
    <Wrapper>
      <StyledButton
        label="Take test"
        variant={isLoading ? 'disabled' : 'filled'}
        onClick={handleOnEnrollClick}
      />
      <StyledButton
        icon={<Image height={24} width={24} src={FileIcon} alt="instruction" />}
        label="Instructions"
        variant="hollow"
        color="black"
        onClick={handleOnInstructionsClick}
      />
    </Wrapper>
  );
};

export default Actions;
